import React, { useState } from 'react';
import { X, Edit, Trash2, Send, Copy, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { templateService } from '../../services/templateService';
import toast from 'react-hot-toast';

const TemplateDetailsModal = ({ 
  isOpen, 
  onClose, 
  template, 
  onEdit, 
  onDelete, 
  onRefresh 
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    if (!template || !window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    setIsDeleting(true);
    try {
      await templateService.deleteTemplate(template.templateId);
      toast.success('Template deleted successfully');
      onDelete();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmitToWhatsApp = async () => {
    if (!template) return;

    setIsSubmitting(true);
    try {
      await templateService.submitTemplateToWhatsApp(template.templateId);
      toast.success('Template submitted to WhatsApp successfully');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyContent = () => {
    if (template?.content) {
      navigator.clipboard.writeText(template.content);
      toast.success('Content copied to clipboard');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'REJECTED':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'DISABLED':
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'text-green-600 bg-green-100';
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-100';
      case 'REJECTED':
        return 'text-red-600 bg-red-100';
      case 'DISABLED':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[50] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            {getStatusIcon(template.templateStatus)}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {template.templateName}
              </h2>
              <p className="text-sm text-gray-600">Template Details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status and Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">
                  Status
                </label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(template.templateStatus)}`}>
                  {template.templateStatus}
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">
                  Category
                </label>
                <span className="text-sm text-gray-500">{template.templateCategory}</span>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">
                  Language
                </label>
                <span className="text-sm text-gray-500">{template.language}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">
                  Template ID
                </label>
                <span className="text-sm text-gray-500 font-mono">{template.templateId}</span>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">
                  Created At
                </label>
                <span className="text-sm text-gray-500">{formatDate(template.createdAt)}</span>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">
                  Last Updated
                </label>
                <span className="text-sm text-gray-500">{formatDate(template.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Template Content */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-bold text-gray-800">
                Template Content
              </label>
              <button
                onClick={handleCopyContent}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </button>
            </div>
            <div className="bg-gray-50 border rounded-lg p-4">
              <pre className="text-sm text-gray-500 whitespace-pre-wrap font-sans">
                {template.content}
              </pre>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            {/* Only show edit for DRAFT and REJECTED templates */}
            {(template.templateStatus === 'DRAFT' || template.templateStatus === 'REJECTED') && (
              <button
                onClick={() => onEdit(template)}
                className="btn-secondary flex items-center"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Template
              </button>
            )}

            {/* Show message for templates that cannot be edited */}
            {(template.templateStatus === 'PENDING' || template.templateStatus === 'APPROVED') && (
              <div className="flex items-center text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-md">
                <AlertCircle className="h-4 w-4 mr-2" />
                <span className="font-bold">Cannot edit template in {template.templateStatus} status</span>
              </div>
            )}

            {template.templateStatus === 'DRAFT' && (
              <button
                onClick={handleSubmitToWhatsApp}
                disabled={isSubmitting}
                className="btn-primary flex items-center disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Submit to WhatsApp
              </button>
            )}

            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center disabled:opacity-50"
            >
              {isDeleting ? (
                <Clock className="h-4 w-4 mr-2 animate-spin text-red-600" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2 text-red-600" />
              )}
              <span className="text-red-600">Delete Template</span>
            </button>
          </div>

          {/* WhatsApp Guidelines */}
          {template.templateStatus === 'DRAFT' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Ready to Submit?</h4>
              <p className="text-sm text-blue-800">
                This template is ready to be submitted to WhatsApp for approval. 
                Once approved, you can use it in your campaigns and messages.
              </p>
            </div>
          )}

          {template.templateStatus === 'PENDING' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">Under Review</h4>
              <p className="text-sm text-yellow-800">
                This template is currently being reviewed by WhatsApp. 
                The review process typically takes 24-48 hours.
              </p>
            </div>
          )}

          {template.templateStatus === 'REJECTED' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-900 mb-2">Template Rejected</h4>
              <p className="text-sm text-red-800">
                This template was rejected by WhatsApp. Please review the content 
                and make necessary changes before resubmitting.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateDetailsModal;
